import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Typography,
} from '@mui/material';
import { DOCUMENT } from '../constants/documentTheme';

async function blobFromVideo(video) {
  // Prefer track settings when available — matches the true capture resolution.
  const track = video.srcObject?.getVideoTracks?.()?.[0];
  const settings = track?.getSettings?.() || {};
  const width = settings.width || video.videoWidth || 1280;
  const height = settings.height || video.videoHeight || 720;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { alpha: false });
  ctx.drawImage(video, 0, 0, width, height);
  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.92);
  });
  canvas.width = 0;
  canvas.height = 0;
  return blob;
}

/**
 * Live webcam capture for desktop (and browsers where file capture= is ignored).
 */
const MemoriesCameraDialog = ({ open, onClose, onCapture }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState('');

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (!open) {
      stopStream();
      setError('');
      setStarting(false);
      setCapturing(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setStarting(true);
      setError('');
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('NO_CAMERA_API');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.name === 'NotAllowedError'
              ? t('memories.cameraPermissionDenied')
              : t('memories.cameraUnavailable')
          );
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, t]);

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setCapturing(true);
    try {
      const blob = await blobFromVideo(video);
      if (!blob) {
        setError(t('memories.cameraUnavailable'));
        return;
      }
      const file = new File([blob], `camera-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      stopStream();
      onCapture(file);
      onClose();
    } catch {
      setError(t('memories.cameraUnavailable'));
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0, backgroundColor: '#111' }}>
        <Box sx={{ position: 'relative', aspectRatio: '4 / 3', backgroundColor: '#000' }}>
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: error || starting ? 'none' : 'block',
            }}
          />
          {(starting || error) && (
            <Box sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3,
              textAlign: 'center',
            }}
            >
              {starting ? (
                <CircularProgress size={36} sx={{ color: DOCUMENT.ivory }} />
              ) : (
                <Typography sx={{ color: DOCUMENT.ivory, fontSize: 14 }}>{error}</Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{
        px: 2,
        py: 1.5,
        backgroundColor: DOCUMENT.surface,
        borderTop: `1px solid ${DOCUMENT.rule}`,
        justifyContent: 'space-between',
      }}
      >
        <Button
          onClick={onClose}
          sx={{ color: DOCUMENT.ink3, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 11 }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleCapture}
          disabled={starting || Boolean(error) || capturing}
          sx={{
            backgroundColor: DOCUMENT.oxblood,
            color: DOCUMENT.ivory,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            fontSize: 11,
            borderRadius: '2px',
            px: 2,
            '&:hover': { backgroundColor: '#5C1A22' },
            '&.Mui-disabled': { backgroundColor: DOCUMENT.oxblood, color: DOCUMENT.ivory, opacity: 0.4 },
          }}
        >
          {capturing ? t('common.loading') : t('memories.capturePhoto')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemoriesCameraDialog;

/** Phones / Appilix: no in-app camera yet — MemoriesUploadPanel hides Take photo. */
export function shouldUseNativeCameraCapture() {
  if (typeof window === 'undefined') return false;
  if (window.appilix) return true;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod/i.test(ua);
}
